import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, PenTool, Code2, Rocket } from 'lucide-react';

const Process = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(0); // First one open by default

  const steps = [
    {
      icon: <Lightbulb className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'Discovery',
      description: 'We dive deep into your business needs, target audience, and goals to create a solid foundation.',
      step: '01'
    },
    {
      icon: <PenTool className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'Design',
      description: 'Our designers create intuitive, stunning interfaces that align perfectly with your brand identity.',
      step: '02'
    },
    {
      icon: <Code2 className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'Development',
      description: 'Clean, scalable code is written and rigorously tested to ensure quality and flawless performance.',
      step: '03'
    },
    {
      icon: <Rocket className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'Launch',
      description: 'We deploy your solution seamlessly and provide ongoing support to ensure long-term success.',
      step: '04'
    }
  ];

  return (
    <section className="bg-svnate-darkNavy py-24 md:py-32 px-6 md:px-12 relative" id="process">
      <div className="container mx-auto relative z-10 max-w-7xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419]">
              <span className="text-svnate-lightTeal font-semibold text-sm">Our Method</span>
            </div>
            <h2 className="text-4xl md:text-7xl font-bold tracking-tight text-white mb-6">
              A Proven Approach
            </h2>
            <p className="text-lg md:text-2xl text-foreground/70 font-serif">
              Our streamlined 4-step process ensures your project is delivered on time, 
              on budget, and exceeds expectations every single time.
            </p>
          </div>
          
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 bg-white/10 text-white rounded-full text-lg font-medium hover:bg-white/20 border border-white/10 transition-all duration-300 self-start md:self-end"
          >
            Start Your Project
          </button>
        </motion.div>

        {/* Giant Editorial Accordion */}
        <div className="border-t border-white/10">
          {steps.map((step, index) => {
            const isHovered = hoveredIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onClick={() => setHoveredIndex(index)}
                className="border-b border-white/10 overflow-hidden cursor-pointer group"
              >
                <div className={`py-8 md:py-16 flex flex-col md:flex-row md:items-center gap-8 md:gap-16 transition-all duration-500 ${isHovered ? 'bg-white/[0.02] px-4 md:px-8 -mx-4 md:-mx-8' : ''}`}>
                  
                  {/* Massive Number */}
                  <div className={`text-6xl md:text-8xl lg:text-[10rem] font-black leading-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isHovered ? 'text-svnate-lightTeal drop-shadow-[0_0_20px_rgba(110,231,183,0.3)]' : 'text-white/5 group-hover:text-white/10'}`}>
                    {step.step}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className={`text-4xl md:text-6xl font-bold tracking-tight transition-colors duration-500 mb-2 ${isHovered ? 'text-white' : 'text-white/30 group-hover:text-white/50'}`}>
                      {step.title}
                    </h3>
                    
                    <AnimatePresence initial={false}>
                      {isHovered && (
                        <motion.div
                          initial={{ height: 0, opacity: 0, y: 20 }}
                          animate={{ height: 'auto', opacity: 1, y: 0 }}
                          exit={{ height: 0, opacity: 0, y: 20 }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        >
                          <p className="text-xl md:text-2xl text-foreground/70 max-w-2xl font-serif mt-6">
                            {step.description}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Icon */}
                  <div className="hidden md:flex w-24 h-24 items-center justify-center">
                    <AnimatePresence mode="wait">
                      {isHovered ? (
                        <motion.div
                          key="icon"
                          initial={{ scale: 0, opacity: 0, rotate: -90 }}
                          animate={{ scale: 1, opacity: 1, rotate: 0 }}
                          exit={{ scale: 0, opacity: 0, rotate: 90 }}
                          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], type: 'spring', bounce: 0.4 }}
                          className="p-6 rounded-full bg-white/5 border border-white/10 backdrop-blur-md"
                        >
                          {step.icon}
                        </motion.div>
                      ) : (
                        <motion.div
                           key="empty"
                           className="w-16 h-px bg-white/10 group-hover:w-24 group-hover:bg-white/20 transition-all duration-500"
                        />
                      )}
                    </AnimatePresence>
                  </div>

                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Process;
